import React, { useState, useRef } from 'react';
import { Image, Video, Music, ExternalLink, Upload, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { optimizeImage, validateImageFile } from '../utils/imageOptimizer';

export type MediaType = 'image' | 'video' | 'audio' | 'external';
export type UploadMethod = 'upload' | 'url';

export interface MediaData {
  type: MediaType;
  url: string;
  method: UploadMethod;
}

interface MediaUploadProps {
  value?: MediaData | null;
  onChange: (media: MediaData | null) => void;
  storageContainer: 'story-media' | 'exclusive-content-media';
  error?: string;
  className?: string;
}

const mediaTypes = [
  { type: 'image' as MediaType, label: 'Image', icon: Image },
  { type: 'video' as MediaType, label: 'Video', icon: Video },
  { type: 'audio' as MediaType, label: 'Audio', icon: Music },
  { type: 'external' as MediaType, label: 'Link', icon: ExternalLink },
];

export function MediaUpload({ 
  value, 
  onChange, 
  storageContainer, 
  error, 
  className = '' 
}: MediaUploadProps) {
  const [selectedType, setSelectedType] = useState<MediaType>(value?.type || 'image');
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>(value?.method || 'upload');
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(value?.url || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTypeChange = (type: MediaType) => {
    setSelectedType(type);
    
    // Reset when changing type
    setUrlInput('');
    onChange(null);
    
    // External links only support URL method
    if (type === 'external') {
      setUploadMethod('url');
    } else if (type === 'image') {
      setUploadMethod('upload'); // Default to upload for images
    } else {
      setUploadMethod('url'); // Video and audio default to URL
    }
  };

  const handleMethodChange = (method: UploadMethod) => {
    setUploadMethod(method);
    setUrlInput('');
    onChange(null);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);

      let file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      const validTypes = {
        image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        video: ['video/mp4', 'video/webm', 'video/ogg'],
        audio: ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a'],
      };

      if (!validTypes[selectedType]?.includes(file.type)) {
        throw new Error(`Please upload a valid ${selectedType} file`);
      }

      // Validate file size (10MB before optimization)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Optimize images before upload
      if (selectedType === 'image') {
        await validateImageFile(file);
        const optimized = await optimizeImage(file, {
          maxWidth: 800,
          maxHeight: 800,
          quality: 0.85,
          format: 'image/webp',
        });
        file = optimized.file;
      }

      // Generate unique file name
      const fileName = `${uuidv4().replace(/-/g, '')}.webp`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(storageContainer)
        .upload(fileName, file, {
          cacheControl: '31536000',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(storageContainer)
        .getPublicUrl(fileName);

      onChange({
        type: selectedType,
        url: publicUrl,
        method: 'upload'
      });

    } catch (err) {
      console.error('Error uploading file:', err);
      // You might want to show this error to the user
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange({
        type: selectedType,
        url: urlInput.trim(),
        method: 'url'
      });
    }
  };

  const handleRemove = () => {
    setUrlInput('');
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getAcceptTypes = () => {
    switch (selectedType) {
      case 'image':
        return 'image/*';
      case 'video':
        return 'video/*';
      case 'audio':
        return 'audio/*';
      default:
        return '';
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Media Type Selector */}
      <div>
        <label className="block text-sm font-medium text-light/80 mb-3">
          Media Type
        </label>
        <div className="grid grid-cols-4 gap-2">
          {mediaTypes.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeChange(type)}
              className={`p-3 rounded-lg border transition-all flex flex-col items-center space-y-2 ${
                selectedType === type
                  ? 'border-light bg-light/10 text-light'
                  : 'border-gray-custom/20 text-light/60 hover:border-gray-custom/40 hover:text-light'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Upload Method Selector (only for images) */}
      {selectedType === 'image' && (
        <div>
          <label className="block text-sm font-medium text-light/80 mb-3">
            Method
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleMethodChange('upload')}
              className={`p-3 rounded-lg border transition-all flex items-center justify-center space-x-2 ${
                uploadMethod === 'upload'
                  ? 'border-light bg-light/10 text-light'
                  : 'border-gray-custom/20 text-light/60 hover:border-gray-custom/40 hover:text-light'
              }`}
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm font-medium">Upload</span>
            </button>
            <button
              type="button"
              onClick={() => handleMethodChange('url')}
              className={`p-3 rounded-lg border transition-all flex items-center justify-center space-x-2 ${
                uploadMethod === 'url'
                  ? 'border-light bg-light/10 text-light'
                  : 'border-gray-custom/20 text-light/60 hover:border-gray-custom/40 hover:text-light'
              }`}
            >
              <ExternalLink className="w-4 h-4" />
              <span className="text-sm font-medium">URL</span>
            </button>
          </div>
        </div>
      )}

      {/* Upload Interface */}
      {uploadMethod === 'upload' && selectedType !== 'external' && (
        <div>
          <label className="block text-sm font-medium text-light/80 mb-2">
            Upload {selectedType}
          </label>
          <div className="space-y-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept={getAcceptTypes()}
              className="hidden"
            />
            
            {!value?.url ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full p-6 border-2 border-dashed border-gray-custom/20 rounded-lg hover:border-gray-custom/40 transition-colors flex flex-col items-center space-y-2 text-light/60 hover:text-light"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8" />
                    <span>Click to upload {selectedType}</span>
                    <span className="text-xs">Max 5MB</span>
                  </>
                )}
              </button>
            ) : (
              <div className="relative">
                <div className="p-4 bg-gray-custom/10 rounded-lg flex items-center space-x-3">
                  <div className="w-12 h-12 bg-light/20 rounded-lg flex items-center justify-center">
                    {selectedType === 'image' && <Image className="w-6 h-6 text-light" />}
                    {selectedType === 'video' && <Video className="w-6 h-6 text-light" />}
                    {selectedType === 'audio' && <Music className="w-6 h-6 text-light" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-light font-medium">File uploaded successfully</p>
                    <p className="text-light/60 text-sm truncate">{value.url}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="p-2 text-light/60 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* URL Interface */}
      {(uploadMethod === 'url' || selectedType === 'external') && (
        <div>
          <label className="block text-sm font-medium text-light/80 mb-2">
            {selectedType === 'external' ? 'External Link URL' : `${selectedType} URL`}
          </label>
          <div className="flex space-x-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={`Enter ${selectedType} URL...`}
              className="flex-1 px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleUrlSubmit}
              disabled={!urlInput.trim()}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
          
          {value?.url && (
            <div className="mt-3 p-4 bg-gray-custom/10 rounded-lg flex items-center space-x-3">
              <div className="w-12 h-12 bg-light/20 rounded-lg flex items-center justify-center">
                {selectedType === 'image' && <Image className="w-6 h-6 text-light" />}
                {selectedType === 'video' && <Video className="w-6 h-6 text-light" />}
                {selectedType === 'audio' && <Music className="w-6 h-6 text-light" />}
                {selectedType === 'external' && <ExternalLink className="w-6 h-6 text-light" />}
              </div>
              <div className="flex-1">
                <p className="text-light font-medium">Media added successfully</p>
                <p className="text-light/60 text-sm truncate">{value.url}</p>
              </div>
              <button
                type="button"
                onClick={handleRemove}
                className="p-2 text-light/60 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}